import { useCallback, useEffect, useRef, useState } from "react";
import { Loader } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  IconBriefcase,
  IconChevronRight,
  IconBuildingStore,
  IconFolder,
  IconFolderHeart,
  IconShare,
  IconTruck,
  IconUserHexagon,
  IconUsers,
} from "@tabler/icons-react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import clsx from "clsx";

import {
  browseFilesHub,
  browseFilesHubEntities,
  FilesHubQueryKeys,
  type FilesHubBrowseKind,
  type FilesHubEntity,
  type FilesHubFolder,
} from "@/api/files-hub/filesHub.queries";
import {
  FileSharesQueryKeys,
  getEntityShareRecords,
  getShareDownloadUrl,
  getSharedByMe,
  getSharedWithMe,
  shareTargetKey,
  type EntityShareRecordType,
  type SharedByMeItem,
  type SharedWithMeItem,
  type ShareRecordType,
  type ShareTarget,
} from "@/api/files-hub/fileShares.api";
import { ShareCard } from "@/components/core/ShareCard";
import { MODULE_KEYS_MAP } from "@/api/permissions/permissions.types";
import { SUPER_ADMIN } from "@/components/data/constants";
import { usePermissions } from "@/hooks/auth/usePermissions";
import { useActiveSession } from "@/hooks/useActiveSession";
import { Button } from "@/components/ui/Button/Button.tsx";
import { SharedFileManager } from "../SharedFileManager";
import { ShareDialog } from "../Modals/ShareModal/ShareDialog";
import { audienceTexts, permissionLabel } from "../shareLabels.utils";
import { InfiniteScrollSentinel } from "./InfiniteScrollSentinel";
import { ShareableCard } from "./ShareableCard";
import type { FileManagerMount } from "../FileManagerMount";
import {
  FileManagerHeaderContainer,
  FileManagerHeaderTitle,
  FileManagerSearchField,
} from "@/components/core/FileManager";
import { FileManagerItemCard } from "@/components/core/FileManager/components/FileManagerItemCard/FileManagerItemCard.tsx";
import type {
  FileManagerItem,
  FileManagerPathAncestor,
  FileManagerPermissions,
} from "@/components/core/FileManager";
import headerStyles from "@/components/core/FileManager/components/FileManagerHeader/FileManagerHeader.module.scss";
import bodyStyles from "@/components/core/FileManager/components/FileManagerBody/FileManagerBody.module.scss";

import styles from "./FilesHub.module.scss";

const PAGE_SIZE = 48;

export type SectionKey =
  | "clients"
  | "service-providers"
  | "suppliers"
  | "work-orders"
  | "shared-by-me"
  | "my"
  | "shared-with-me"
  | "registry";

interface SectionDef {
  key: SectionKey;
  label: string;
  description: string;
  icon: typeof IconFolder;
  browseKind?: FilesHubBrowseKind;
  /** Clients and work orders live under an entity, so the hub browses entities first. */
  nestsUnderEntity?: boolean;
  group: string;
}

const SECTIONS: SectionDef[] = [
  {
    key: "my",
    label: "My files",
    description: "Private unless you share it",
    icon: IconFolderHeart,
    group: "Drives",
  },
  {
    key: "shared-by-me",
    label: "Shared files",
    description: "Files and folders you shared",
    icon: IconShare,
    group: "Drives",
  },
  {
    key: "shared-with-me",
    label: "Shared with me",
    description: "Items other people shared with you",
    icon: IconUsers,
    group: "Drives",
  },
  {
    key: "clients",
    label: "Clients",
    description: "One folder per client",
    icon: IconBuildingStore,
    browseKind: "clients",
    nestsUnderEntity: true,
    group: "Records",
  },
  {
    key: "service-providers",
    label: "Service providers",
    description: "One folder per provider",
    icon: IconUserHexagon,
    browseKind: "service-providers",
    group: "Records",
  },
  {
    key: "suppliers",
    label: "Suppliers",
    description: "One folder per supplier",
    icon: IconTruck,
    browseKind: "suppliers",
    group: "Records",
  },
  {
    key: "work-orders",
    label: "Work orders",
    description: "One folder per work order",
    icon: IconBriefcase,
    browseKind: "work-orders",
    nestsUnderEntity: true,
    group: "Records",
  },
];

interface OpenFolder {
  id: string;
  name: string;
  entityId?: string | null;
}

interface CardShare {
  target: ShareTarget;
  name: string;
}

/**
 * Mirrors who the backend lets share a record: entities, clients and work orders
 * only from inside their own entity (or as a super admin outside any entity) and
 * with the module they belong to; suppliers and service providers to any Files
 * editor. The backend still decides; this only keeps the action off where it
 * would always be refused.
 */
function useCanShareRecord(): (kind: ShareRecordType, entityId: string | null) => boolean {
  const { userType, isEntityLogin, tenantId } = useActiveSession();
  const { hasAccess } = usePermissions();
  const filesEdit = hasAccess({ module: MODULE_KEYS_MAP.files }).edit;
  const clientView = hasAccess({ module: MODULE_KEYS_MAP.clients }).view;
  const workOrderView = hasAccess({ module: MODULE_KEYS_MAP.workOrders }).view;
  const unscopedSuperAdmin = userType === SUPER_ADMIN && !isEntityLogin;

  return (kind, entityId) => {
    if (!filesEdit) return false;
    const opensEntity =
      unscopedSuperAdmin || (isEntityLogin && !!entityId && entityId === tenantId);
    switch (kind) {
      case "entities":
        return opensEntity && clientView && workOrderView;
      case "clients":
        return opensEntity && clientView;
      case "work-orders":
        return opensEntity && workOrderView;
      default:
        return true;
    }
  };
}

function HubCard({
  item,
  subtitle,
  shareTarget,
  onShare,
  onOpen,
}: {
  item: FileManagerItem;
  subtitle: string;
  shareTarget: ShareTarget | null;
  onShare: (share: CardShare) => void;
  onOpen: () => void;
}) {
  const card = (
    <FileManagerItemCard item={item} subtitle={subtitle} showMenu={!!shareTarget} onOpen={onOpen} />
  );
  if (!shareTarget) return card;
  return (
    <ShareableCard onShare={() => onShare({ target: shareTarget, name: item.name })}>
      {card}
    </ShareableCard>
  );
}

export function parseSectionKey(value: unknown): SectionKey | undefined {
  return SECTIONS.find((s) => s.key === value)?.key;
}

export interface FilesHubDeepLink {
  section?: SectionKey;
  share?: string;
}

export function FilesHub({
  deepLink,
  onDeepLinkConsumed,
}: {
  deepLink?: FilesHubDeepLink;
  onDeepLinkConsumed?: () => void;
}) {
  const [section, setSection] = useState<SectionDef>(
    () =>
      SECTIONS.find((s) => s.key === deepLink?.section) ??
      SECTIONS.find((s) => s.key === "clients") ??
      SECTIONS[0],
  );
  const [folder, setFolder] = useState<OpenFolder | null>(null);
  const [entity, setEntity] = useState<OpenFolder | null>(null);

  const deepLinkSection = deepLink?.section;
  const deepLinkShare = deepLink?.share;
  const hasDeepLink = !!deepLink;
  useEffect(() => {
    if (!hasDeepLink) return;
    const target = SECTIONS.find((s) => s.key === deepLinkSection);
    if (target) {
      setSection(target);
      setFolder(null);
      setEntity(null);
    }
    // A share is consumed by the Shared with me section once its list has loaded.
    if (!deepLinkShare || target?.key !== "shared-with-me") onDeepLinkConsumed?.();
  }, [hasDeepLink, deepLinkSection, deepLinkShare, onDeepLinkConsumed]);

  const handleSectionChange = (s: SectionDef) => {
    setSection(s);
    setFolder(null);
    setEntity(null);
  };

  const groups = SECTIONS.reduce<Record<string, SectionDef[]>>((acc, s) => {
    (acc[s.group] ??= []).push(s);
    return acc;
  }, {});

  return (
    <div className={styles.hub}>
      <nav className={styles.sidebar}>
        {Object.entries(groups).map(([groupName, items]) => (
          <div key={groupName} className={styles.navGroup}>
            <span className={styles.navGroupLabel}>{groupName}</span>
            {items.map((s) => (
              <button
                key={s.key}
                type="button"
                className={clsx(styles.navItem, section.key === s.key && styles.navItemActive)}
                onClick={() => handleSectionChange(s)}
              >
                <s.icon size={18} className={styles.navIcon} />
                <span>{s.label}</span>
              </button>
            ))}
          </div>
        ))}
      </nav>

      <div className={styles.main}>
        <SectionView
          section={section}
          folder={folder}
          entity={entity}
          onOpenFolder={setFolder}
          onClearFolder={() => setFolder(null)}
          onOpenEntity={setEntity}
          onClearEntity={() => {
            setEntity(null);
            setFolder(null);
          }}
          deepLinkShareId={deepLinkShare}
          onDeepLinkConsumed={onDeepLinkConsumed}
        />
      </div>
    </div>
  );
}

function SectionView({
  section,
  folder,
  entity,
  onOpenFolder,
  onClearFolder,
  onOpenEntity,
  onClearEntity,
  headerLeading,
  deepLinkShareId,
  onDeepLinkConsumed,
}: {
  section: SectionDef;
  folder: OpenFolder | null;
  entity: OpenFolder | null;
  onOpenFolder: (f: OpenFolder) => void;
  onClearFolder: () => void;
  onOpenEntity: (e: OpenFolder) => void;
  onClearEntity: () => void;
  headerLeading?: React.ReactNode;
  deepLinkShareId?: string;
  onDeepLinkConsumed?: () => void;
}) {
  if (section.key === "my") {
    return (
      <div className={styles.mainBodyFull}>
        <MountedManager mount={{ type: "user" }} title="My files" writable shareable ownDrive />
      </div>
    );
  }

  if (section.key === "shared-with-me") {
    return (
      <div className={styles.mainBodyFull}>
        <SharedWithMeSection
          section={section}
          deepLinkShareId={deepLinkShareId}
          onDeepLinkConsumed={onDeepLinkConsumed}
        />
      </div>
    );
  }

  if (section.key === "shared-by-me") {
    return (
      <div className={styles.mainBodyFull}>
        <SharedByMeSection section={section} />
      </div>
    );
  }

  if (folder) {
    const ancestors: FileManagerPathAncestor[] = [{ label: section.label, onClick: onClearEntity }];
    if (entity) ancestors.push({ label: entity.name, onClick: onClearFolder });
    return (
      <div className={styles.mainBodyFull}>
        <FolderManager
          section={section}
          folder={folder}
          headerLeading={headerLeading}
          ancestors={ancestors}
        />
      </div>
    );
  }

  if (section.nestsUnderEntity && !entity) {
    return (
      <div className={styles.mainBodyFull}>
        <EntityBrowser section={section} onOpenEntity={onOpenEntity} />
      </div>
    );
  }

  return (
    <div className={styles.mainBodyFull}>
      <FolderBrowser
        section={section}
        entity={entity}
        onOpenFolder={onOpenFolder}
        onClearEntity={onClearEntity}
      />
    </div>
  );
}

function HubHeaderPath({ trail }: { trail: { label: string; onClick?: () => void }[] }) {
  const lastIndex = trail.length - 1;
  return (
    <h3
      data-slot="file-manager-header-title"
      className={headerStyles.title}
      aria-label="Folder path"
    >
      {trail.map((segment, i) => (
        <span key={segment.label} className={headerStyles.pathEntry}>
          {i > 0 && <IconChevronRight size={16} className={headerStyles.pathSeparator} />}
          <button
            type="button"
            className={clsx(headerStyles.pathSegment, i === lastIndex && headerStyles.pathCurrent)}
            onClick={segment.onClick}
            disabled={i === lastIndex}
          >
            {segment.label}
          </button>
        </span>
      ))}
    </h3>
  );
}

function EntityBrowser({
  section,
  onOpenEntity,
}: {
  section: SectionDef;
  onOpenEntity: (entity: OpenFolder) => void;
}) {
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 350);
  const canShareRecord = useCanShareRecord();
  const [cardShare, setCardShare] = useState<CardShare | null>(null);

  // No page in the key: each page would otherwise be its own cache entry and nothing would
  // accumulate. debouncedSearch stays, so a new search still starts a fresh list from page 1 --
  // which is also why the old setPage(1) calls on the search box are gone.
  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey: [FilesHubQueryKeys.entities, debouncedSearch],
    queryFn: ({ pageParam, signal }) =>
      browseFilesHubEntities(
        { page: pageParam, limit: PAGE_SIZE, search: debouncedSearch || undefined },
        signal,
      ),
    initialPageParam: 1,
    // The endpoint reports a total rather than a next cursor, so "is there more" is "have we
    // received fewer rows than the total". Comparing loaded length against total also stops the
    // fetching when a final page comes back short.
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((count, page) => count + page.items.length, 0);
      return loaded < lastPage.total ? allPages.length + 1 : undefined;
    },
  });

  const entities: FilesHubEntity[] = data?.pages.flatMap((p) => p.items) ?? [];

  const cardItems: FileManagerItem[] = entities.map((e: FilesHubEntity) => ({
    id: e.id,
    name: e.name,
    type: "FOLDER",
    parentId: null,
    s3Key: null,
    contentType: null,
    fileSize: null,
    tags: [],
    childCount: section.key === "clients" ? e.clientCount : e.workOrderCount,
    createdAt: "",
    updatedAt: "",
  }));

  const cardSubtitle = (e: FilesHubEntity) => {
    const count = section.key === "clients" ? e.clientCount : e.workOrderCount;
    const noun = section.key === "clients" ? "client" : "work order";
    return `${count} ${noun}${count === 1 ? "" : "s"}`;
  };

  return (
    <>
      <FileManagerHeaderContainer>
        <FileManagerHeaderTitle>{section.label}</FileManagerHeaderTitle>
        <div className={headerStyles.actions}>
          <FileManagerSearchField
            placeholder="Search entities..."
            value={search}
            onChange={(value: string) => setSearch(value)}
          />
        </div>
      </FileManagerHeaderContainer>

      <div className={bodyStyles.fileManagerBody}>
        {isLoading && !data ? (
          <div className={styles.loadingState}>
            <Loader size="sm" />
          </div>
        ) : cardItems.length === 0 ? (
          <div className={styles.emptyState}>Nothing here yet.</div>
        ) : (
          <>
            <div className={bodyStyles.grid}>
              {cardItems.map((item, i) => (
                <HubCard
                  key={item.id}
                  item={item}
                  // Indexes the flattened list, not one page: reading data.items[i] here would
                  // pull the wrong entity's counts as soon as a second page landed.
                  subtitle={cardSubtitle(entities[i]!)}
                  shareTarget={
                    canShareRecord("entities", item.id)
                      ? { kind: "record", recordType: "entities", recordId: item.id }
                      : null
                  }
                  onShare={setCardShare}
                  onOpen={() => onOpenEntity({ id: item.id, name: item.name })}
                />
              ))}
            </div>
            <InfiniteScrollSentinel
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              fetchNextPage={fetchNextPage}
            />
          </>
        )}
      </div>
      <ShareDialog
        target={cardShare?.target ?? null}
        name={cardShare?.name ?? ""}
        onClose={() => setCardShare(null)}
      />
    </>
  );
}

function FolderBrowser({
  section,
  entity,
  onOpenFolder,
  onClearEntity,
}: {
  section: SectionDef;
  entity: OpenFolder | null;
  onOpenFolder: (folder: OpenFolder) => void;
  onClearEntity: () => void;
}) {
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 350);
  const canShareRecord = useCanShareRecord();
  const [cardShare, setCardShare] = useState<CardShare | null>(null);

  // See EntityBrowser: page stays out of the key so pages accumulate; entity and search stay in
  // it so switching entity or searching starts a fresh list.
  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey: [FilesHubQueryKeys.browse, section.browseKind, entity?.id, debouncedSearch],
    queryFn: ({ pageParam, signal }) =>
      browseFilesHub(
        section.browseKind as FilesHubBrowseKind,
        {
          page: pageParam,
          limit: PAGE_SIZE,
          search: debouncedSearch || undefined,
          entityId: entity?.id,
        },
        signal,
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((count, page) => count + page.items.length, 0);
      return loaded < lastPage.total ? allPages.length + 1 : undefined;
    },
  });

  const folders: FilesHubFolder[] = data?.pages.flatMap((p) => p.items) ?? [];

  const cardItems: FileManagerItem[] = folders.map((f: FilesHubFolder) => ({
    id: f.id,
    name: f.name,
    type: "FOLDER",
    parentId: null,
    s3Key: null,
    contentType: null,
    fileSize: null,
    tags: [],
    childCount: f.fileCount,
    createdAt: "",
    updatedAt: "",
  }));

  const cardSubtitle = (f: FilesHubFolder) =>
    [f.subtitle, `${f.fileCount} ${f.fileCount === 1 ? "file" : "files"}`]
      .filter(Boolean)
      .join(" · ");

  return (
    <>
      <FileManagerHeaderContainer>
        {entity ? (
          <HubHeaderPath
            trail={[{ label: section.label, onClick: onClearEntity }, { label: entity.name }]}
          />
        ) : (
          <FileManagerHeaderTitle>{section.label}</FileManagerHeaderTitle>
        )}
        <div className={headerStyles.actions}>
          <FileManagerSearchField
            placeholder={`Search ${section.label.toLowerCase()}...`}
            value={search}
            onChange={(value: string) => setSearch(value)}
          />
        </div>
      </FileManagerHeaderContainer>

      <div className={bodyStyles.fileManagerBody}>
        {isLoading && !data ? (
          <div className={styles.loadingState}>
            <Loader size="sm" />
          </div>
        ) : cardItems.length === 0 ? (
          <div className={styles.emptyState}>Nothing here yet.</div>
        ) : (
          <>
            <div className={bodyStyles.grid}>
              {cardItems.map((item, i) => (
                <HubCard
                  key={item.id}
                  item={item}
                  subtitle={cardSubtitle(folders[i]!)}
                  shareTarget={
                    section.browseKind &&
                    canShareRecord(section.browseKind as ShareRecordType, folders[i]!.entityId)
                      ? {
                          kind: "record",
                          recordType: section.browseKind as ShareRecordType,
                          recordId: item.id,
                        }
                      : null
                  }
                  onShare={setCardShare}
                  onOpen={() =>
                    onOpenFolder({ id: item.id, name: item.name, entityId: folders[i]!.entityId })
                  }
                />
              ))}
            </div>
            <InfiniteScrollSentinel
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              fetchNextPage={fetchNextPage}
            />
          </>
        )}
      </div>
      <ShareDialog
        target={cardShare?.target ?? null}
        name={cardShare?.name ?? ""}
        onClose={() => setCardShare(null)}
      />
    </>
  );
}

function FolderManager({
  section,
  folder,
  headerLeading,
  ancestors,
}: {
  section: SectionDef;
  folder: OpenFolder;
  headerLeading?: React.ReactNode;
  ancestors?: FileManagerPathAncestor[];
}) {
  const mountBySection: Partial<Record<SectionKey, FileManagerMount>> = {
    clients: { type: "client", id: folder.id },
    "work-orders": { type: "work-order", id: folder.id },
    suppliers: { type: "supplier", id: folder.id },
    "service-providers": { type: "sp-docs", id: folder.id },
  };
  const canShareRecord = useCanShareRecord();
  const mount = mountBySection[section.key];
  if (!mount) return null;
  const writable = section.key !== "service-providers";
  const shareable =
    !!section.browseKind &&
    canShareRecord(section.browseKind as ShareRecordType, folder.entityId ?? null);
  return (
    <MountedManager
      mount={mount}
      title={folder.name}
      writable={writable}
      shareable={shareable}
      headerLeading={headerLeading}
      ancestors={ancestors}
    />
  );
}

function isShareReadOnly(share: SharedWithMeItem): boolean {
  return (
    share.permission !== "EDIT" ||
    (share.target.kind === "record" && share.target.recordType === "service_provider")
  );
}

function SharedWithMeSection({
  section,
  deepLinkShareId,
  onDeepLinkConsumed,
}: {
  section: SectionDef;
  deepLinkShareId?: string;
  onDeepLinkConsumed?: () => void;
}) {
  const [openShare, setOpenShare] = useState<SharedWithMeItem | null>(null);
  const { data, isLoading, isFetching } = useQuery({
    queryKey: [FileSharesQueryKeys.sharedWithMe],
    queryFn: ({ signal }) => getSharedWithMe(signal),
  });

  const handleOpen = useCallback(async (share: SharedWithMeItem) => {
    if (share.item.type === "FOLDER") {
      setOpenShare(share);
      return;
    }
    try {
      window.open(await getShareDownloadUrl(share.shareId, share.item.id), "_blank", "noopener");
    } catch {
      notifications.show({
        title: "Download failed",
        message: "Could not open the file. Please try again.",
        color: "red",
      });
    }
  }, []);

  const handledDeepLink = useRef<string | null>(null);
  useEffect(() => {
    if (!deepLinkShareId || isFetching || handledDeepLink.current === deepLinkShareId) return;
    handledDeepLink.current = deepLinkShareId;
    const share = data?.find((s) => s.shareId === deepLinkShareId);
    if (share) void handleOpen(share);
    onDeepLinkConsumed?.();
  }, [deepLinkShareId, isFetching, data, onDeepLinkConsumed, handleOpen]);

  const backToList = { label: section.label, onClick: () => setOpenShare(null) };

  if (openShare) {
    if (openShare.target.kind === "record" && openShare.target.recordType === "entity") {
      return <EntityShareView share={openShare} ancestors={[backToList]} />;
    }
    return (
      <MountedManager
        mount={{
          type: "shared-with-me",
          id: openShare.shareId,
          readOnly: isShareReadOnly(openShare),
        }}
        title={openShare.item.name}
        writable={!isShareReadOnly(openShare)}
        ancestors={[backToList]}
      />
    );
  }

  const shares = data ?? [];

  return (
    <>
      <FileManagerHeaderContainer>
        <FileManagerHeaderTitle>{section.label}</FileManagerHeaderTitle>
      </FileManagerHeaderContainer>

      <div className={bodyStyles.fileManagerBody}>
        {isLoading ? (
          <div className={styles.loadingState}>
            <Loader size="sm" />
          </div>
        ) : shares.length === 0 ? (
          <div className={styles.emptyState}>Nothing has been shared with you yet.</div>
        ) : (
          <div className={bodyStyles.grid}>
            {shares.map((share) => (
              <ShareCard
                key={share.shareId}
                item={share.item}
                subtitle={`Shared by ${share.sharedBy.name} · ${permissionLabel(
                  isShareReadOnly(share) ? "VIEW" : "EDIT",
                )}`}
                onOpen={() => void handleOpen(share)}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function sharedByMeLinkLine(link: NonNullable<SharedByMeItem["link"]>, entityName: string): string {
  const { linkSummary } = audienceTexts(entityName)[link.audience];
  if (link.audience === "RESTRICTED") return linkSummary;
  const roleCount = link.roleIds.length;
  const who =
    link.audience === "ENTITY_ROLES" && roleCount > 0
      ? `People with ${roleCount} ${roleCount === 1 ? "role" : "roles"}`
      : linkSummary;
  return `${who} · ${permissionLabel(link.permission)}`;
}

function sharedByMeSubtitle({ recipients, link }: SharedByMeItem, entityName: string): string {
  const linkLine = link ? sharedByMeLinkLine(link, entityName) : "";
  if (link && recipients.length === 0) return linkLine;
  const count = recipients.length;
  const permissions = new Set(recipients.map((r) => r.permission));
  const access =
    permissions.size > 1
      ? "Mixed access"
      : permissionLabel(permissions.has("EDIT") ? "EDIT" : "VIEW");
  const people = `Shared with ${count} ${count === 1 ? "person" : "people"} · ${access}`;
  return link ? `${people} · ${linkLine}` : people;
}

function sharedByMeTarget({ target, ownDrive }: SharedByMeItem): ShareTarget {
  return target.kind === "item" ? { ...target, ownDrive } : target;
}

function SharedByMeSection({ section }: { section: SectionDef }) {
  const { entityName } = useActiveSession();
  const [managing, setManaging] = useState<CardShare | null>(null);
  const { data, isLoading, isError } = useQuery({
    queryKey: [FileSharesQueryKeys.sharedByMe],
    queryFn: ({ signal }) => getSharedByMe(signal),
  });

  const shares = data ?? [];
  const manage = (share: SharedByMeItem) =>
    setManaging({ target: sharedByMeTarget(share), name: share.item.name });

  return (
    <>
      <FileManagerHeaderContainer>
        <FileManagerHeaderTitle>{section.label}</FileManagerHeaderTitle>
      </FileManagerHeaderContainer>

      <div className={bodyStyles.fileManagerBody}>
        {isLoading ? (
          <div className={styles.loadingState}>
            <Loader size="sm" />
          </div>
        ) : isError ? (
          <div className={styles.emptyState}>Could not load the files you shared.</div>
        ) : shares.length === 0 ? (
          <div className={styles.emptyState}>You haven&apos;t shared anything yet.</div>
        ) : (
          <div className={bodyStyles.grid}>
            {shares.map((share) => (
              <ShareableCard
                key={shareTargetKey(share.target)}
                label="Manage access"
                onShare={() => manage(share)}
              >
                <ShareCard
                  item={share.item}
                  subtitle={sharedByMeSubtitle(share, entityName ?? "this entity")}
                  showMenu
                  onOpen={() => manage(share)}
                />
              </ShareableCard>
            ))}
          </div>
        )}
      </div>

      <ShareDialog
        target={managing?.target ?? null}
        name={managing?.name ?? ""}
        onClose={() => setManaging(null)}
      />
    </>
  );
}

const ENTITY_RECORD_TABS: { type: EntityShareRecordType; label: string }[] = [
  { type: "clients", label: "Clients" },
  { type: "work-orders", label: "Work orders" },
];

function EntityShareView({
  share,
  ancestors,
}: {
  share: SharedWithMeItem;
  ancestors: FileManagerPathAncestor[];
}) {
  const [recordType, setRecordType] = useState<EntityShareRecordType>("clients");
  const [openRecord, setOpenRecord] = useState<OpenFolder | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 350);

  // Same paging as FolderBrowser: page stays out of the key so pages accumulate.
  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey: [FileSharesQueryKeys.entityRecords, share.shareId, recordType, debouncedSearch],
    queryFn: ({ pageParam, signal }) =>
      getEntityShareRecords(
        share.shareId,
        recordType,
        { page: pageParam, limit: PAGE_SIZE, search: debouncedSearch || undefined },
        signal,
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((count, page) => count + page.items.length, 0);
      return loaded < lastPage.total ? allPages.length + 1 : undefined;
    },
    enabled: !openRecord,
  });

  if (openRecord) {
    return (
      <MountedManager
        mount={{
          type: "shared-with-me",
          id: share.shareId,
          readOnly: isShareReadOnly(share),
          record: { type: recordType, id: openRecord.id },
        }}
        title={openRecord.name}
        writable={!isShareReadOnly(share)}
        ancestors={[...ancestors, { label: share.item.name, onClick: () => setOpenRecord(null) }]}
      />
    );
  }

  const records: FilesHubFolder[] = data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <>
      <FileManagerHeaderContainer>
        <HubHeaderPath trail={[...ancestors, { label: share.item.name }]} />
        <div className={headerStyles.actions}>
          <div className={styles.recordTabs}>
            {ENTITY_RECORD_TABS.map((tab) => (
              <Button
                key={tab.type}
                type="button"
                variant={tab.type === recordType ? "primary" : "neutral-secondary"}
                onClick={() => setRecordType(tab.type)}
              >
                {tab.label}
              </Button>
            ))}
          </div>
          <FileManagerSearchField
            placeholder="Search..."
            value={search}
            onChange={(value: string) => setSearch(value)}
          />
        </div>
      </FileManagerHeaderContainer>

      <div className={bodyStyles.fileManagerBody}>
        {isLoading ? (
          <div className={styles.loadingState}>
            <Loader size="sm" />
          </div>
        ) : records.length === 0 ? (
          <div className={styles.emptyState}>Nothing here yet.</div>
        ) : (
          <div className={bodyStyles.grid}>
            {records.map((record) => (
              <FileManagerItemCard
                key={record.id}
                item={{
                  id: record.id,
                  name: record.name,
                  type: "FOLDER",
                  parentId: null,
                  s3Key: null,
                  contentType: null,
                  fileSize: null,
                  tags: [],
                  childCount: record.fileCount,
                  createdAt: "",
                  updatedAt: "",
                }}
                subtitle={[
                  record.subtitle,
                  `${record.fileCount} ${record.fileCount === 1 ? "file" : "files"}`,
                ]
                  .filter(Boolean)
                  .join(" · ")}
                showMenu={false}
                onOpen={() => setOpenRecord({ id: record.id, name: record.name })}
              />
            ))}
          </div>
        )}
        <InfiniteScrollSentinel
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
        />
      </div>
    </>
  );
}

function MountedManager({
  mount,
  title,
  writable,
  shareable = false,
  ownDrive = false,
  headerLeading,
  ancestors,
}: {
  mount: FileManagerMount;
  title: string;
  writable: boolean;
  shareable?: boolean;
  /** My files: the caller's own drive, managed with `files` VIEW access. */
  ownDrive?: boolean;
  headerLeading?: React.ReactNode;
  ancestors?: FileManagerPathAncestor[];
}) {
  const { hasAccess } = usePermissions();
  const { view, create, edit } = hasAccess({ module: MODULE_KEYS_MAP.files });
  const canCreate = ownDrive ? view : create;
  const canEdit = ownDrive ? view : edit;
  const permissions: FileManagerPermissions = {
    ...(writable ? { canCreate, canEdit, canDelete: canEdit, ownerCanDelete: ownDrive } : {}),
    canShare: shareable && canEdit,
  };
  return (
    <SharedFileManager
      mount={mount}
      title={title}
      permissions={permissions}
      urlState={false}
      headerLeading={headerLeading}
      headerAncestors={ancestors}
      fillHeight
    />
  );
}
